import { useEffect, useState } from "react";

/** Matches the homeowner map's cap (`features/map/useGeolocation.ts`) so a
 *  hanging fix cannot leave the caller waiting indefinitely. */
const TIMEOUT_MS = 10_000;

export type GeoStatus = "locating" | "located" | "denied" | "unavailable";

export interface GeoState {
  status: GeoStatus;
  /**
   * Only set once a real browser fix arrives — deliberately no fallback
   * coordinates. Unlike the homeowner map, there is nothing here for a
   * denied/failed fix to approximate to: contractor-api already falls back to
   * this contractor's own stored profile location when lat/lng are omitted,
   * so "no browser fix" still gets a server-computed distance, just not a
   * fresher one. See useGeolocation.ts on the homeowner site for why a
   * fabricated fallback (e.g. Johannesburg) must never take its place.
   */
  position: { lat: number; lng: number } | null;
}

/** The contractor's live position, for a display-only `?lat&lng` on the jobs
 *  and bookings reads — never a filter, never sent anywhere but those GETs. */
export function useGeolocation(): GeoState {
  const [state, setState] = useState<GeoState>({ status: "locating", position: null });

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setState({ status: "unavailable", position: null });
      return;
    }

    let done = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (done) return;
        done = true;
        setState({
          status: "located",
          position: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        });
      },
      (err) => {
        if (done) return;
        done = true;
        // PERMISSION_DENIED is 1; everything else (unavailable, timeout) is a
        // failure the contractor did not choose.
        setState({ status: err.code === 1 ? "denied" : "unavailable", position: null });
      },
      { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: 60_000 },
    );

    return () => {
      done = true;
    };
  }, []);

  return state;
}
